import React, {JSX} from 'react';
import {Form, Spinner} from "react-bootstrap";
import "./SangerQCHistoryTable.css"
import { Storage } from 'components/CommonFunc/amplifyStorageCompat';
import { API } from 'components/CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import {sangerQcApiConsts} from "./Const";
import {errorMessage} from "../Alerts/Const";
import {SangerQCPipeline} from "./Const";
import {
    ColumnDef,
} from "@tanstack/react-table";
import {SangerQCHistoryTableRow, TanStackSangerQCHistoryTable} from "./TanStackSangerQCHistoryTable";

const logger = new ConsoleLogger("SangerQCHistoryTable.tsx")

interface SangerQCHistoryTableProps {
    showSangerQCReport(rowIndex: string): void
    refreshToken: string
    showErrorAlert(message: string): void
}

interface SangerQCHistoryTableState {
    rows: SangerQCHistoryTableRow[]
    loading: boolean
    slNumberOptions: { value: number; label: string }[]
}

export class SangerQCHistoryTable extends React.Component<SangerQCHistoryTableProps, SangerQCHistoryTableState> {
    constructor(props: SangerQCHistoryTableProps) {
        super(props);
        this.state = {
            loading: false,
            rows: [{
                description: "loading",
                uuid: "loading",
                userName: "loading",
                stage: "loading",
                slNumber: "loading",
                timestamp: "loading",
                status: "loading",
                errorMessage: "loading",
                deleted: false,
                s3URL: "loading",
                batchJobURL: "loading",
            }],
            slNumberOptions: [],
        }
    }

    componentDidMount() {
        this.listSlNumbers()
        this.listAllPipelines()
    }

    listSlNumbers() {
        Storage
            .list("sl-config/", {
                level: "public"
            }).then((response: any) => {
                const items = Array.isArray(response) ? response : response?.results ?? []
                const slNumbers: {value: number; label: string}[] = []
                for (let i = 0; i < items.length; i++) {
                    const key = items[i]?.key
                    if (!key || key === "sl-config/") continue
                    const parts = key.toString().split("/")
                    const slStr: string = parts.length > 1 ? parts[1] : ""
                    const slNumber: number = parseInt(slStr.charAt(2))
                    if (!Number.isNaN(slNumber)) {
                        const exists = slNumbers.some(e => e.value === slNumber)
                        if (!exists) {
                            slNumbers.push({value: slNumber, label: slStr.charAt(2)})
                        }
                    }
                }

                this.setState({slNumberOptions: slNumbers})
            }).catch((err: any) => {
                logger.error(err)
                this.props.showErrorAlert(errorMessage(err))
            })
    }

    listAllPipelines() {
        this.setState({
            loading: true
        })

        API.get(sangerQcApiConsts.API_NAME, sangerQcApiConsts.Path.LIST_ALL, {}).then(response => {
            logger.info("Response from API", response)
            this.setState({
                rows: response.data
            })
        }).catch(err => {
            this.props.showErrorAlert(err + ". Please refresh and try again.")
        }).finally(() => {
            this.setState({
                loading: false
            })
        })
    }

    componentDidUpdate(prevProps: Readonly<SangerQCHistoryTableProps>) {
        if (this.props.refreshToken !== prevProps.refreshToken) {
            logger.info("Refresh token changed, refresh SangerQCHistoryTable")
            this.listAllPipelines()
        }
    }

    createTextFilterComponent(placeholder: string): (props: {column: any}) => JSX.Element {
        return ({column}) => (
            <div>
                <input
                    type="text"
                    value={column.getFilterValue() || ''}
                    onChange={e => column.setFilterValue(e.target.value)}
                    placeholder={placeholder}
                    className={`form-control form-control-sm`}
                    style={{ width: '100%' }}
                />
            </div>
        )
    }

    createSelectFilterComponent(
        options: Array<{ value: string | number, label: string }> | Record<string, string>,
        placeholder: string,
    ): (props: {column: any}) => JSX.Element {
        return ({column}) => {
            const selectOptions = Array.isArray(options) ? options : Object.entries(options).map(
                ([value, label]) => ({value, label})
            )

            return (
                <Form.Select
                    size="sm"
                    value={column.getFilterValue() || ''}
                    onChange={e => column.setFilterValue(e.target.value)}
                    className="form-select"
                >
                    <option value="">{placeholder}</option>
                    {selectOptions.map((option) => (
                        <option key={String(option.value)} value={String(option.value)}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
            )
        }
    }

    getColumns(): ColumnDef<SangerQCHistoryTableRow, any>[] {
        const {slNumberOptions} = this.state

        return [
            {
                accessorKey: 'timestamp',
                header: 'Date',
                cell: info => {
                    const timestamp = info.getValue() as string
                    return timestamp ? new Date(timestamp).toLocaleDateString() : ''
                },
                enableSorting: true,
                enableColumnFilter: false,
            },
            {
                accessorKey: 'uuid',
                header: 'Report ID',
                enableSorting: false,
                enableColumnFilter: false,
                meta: {hidden: true},
            },
            {
                accessorKey: 'description',
                header: '',
                enableSorting: false,
                enableColumnFilter: true,
                meta: {filterComponent: this.createTextFilterComponent('Search description')},
            },
            {
                accessorKey: 'userName',
                header: '',
                enableSorting: false,
                enableColumnFilter: true,
                meta: {filterComponent: this.createTextFilterComponent('Search user')},
            },
            {
                accessorKey: 'slNumber',
                header: '',
                enableSorting: false,
                enableColumnFilter: true,
                meta: {filterComponent: this.createSelectFilterComponent(slNumberOptions, 'SL')},
            },
            {
                accessorKey: 'stage',
                header: '',
                enableSorting: false,
                enableColumnFilter: true,
                meta: {filterComponent: this.createSelectFilterComponent(
                        {"Intermediate": "Intermediate", "Final": "Final"},
                        'All stages'
                    ),
                },
            },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: info => {
                    const status = info.getValue() as string
                    switch (status.toLowerCase()) {
                        case SangerQCPipeline.Status.IN_PROGRESS.toLowerCase():
                            return (<span className="text-primary fw-bold">{status}</span>)
                        case SangerQCPipeline.Status.COMPLETED.toLowerCase():
                            return (<span className="text-success fw-bold">{status}</span>)
                        case SangerQCPipeline.Status.FAILED.toLowerCase():
                            return (<span className="text-danger fw-bold">{status}</span>)
                        default:
                            return (<span className="text-dark fw-bold">{status}</span>)
                    }
                },
                enableSorting: false,
                enableColumnFilter: false,
            }
        ]
    }

    render() {
        if (this.state.loading) {
            return (<>
                <Spinner
                    animation="border"
                    size="sm"
                    variant="primary"
                >
                </Spinner>
                <span className="text-primary m-2 fw-bold">Loading all jobs ...</span>
            </>)
        }

        return (
            <>
                <h3>All submitted jobs</h3>
                <TanStackSangerQCHistoryTable
                    columns={this.getColumns()}
                    data={this.state.rows}
                    onRowClick={this.props.showSangerQCReport}
                />
            </>
        );
    }
}
